import {Component} from '@angular/core';
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {Router, RouterLink} from "@angular/router";
import {Teacher} from "../interfaces/teacher";
import {GrantService} from "../services/grant.service";
import {Grant} from "../interfaces/grant";
import {TeacherService} from "../services/teacher.service";

@Component({
  selector: 'app-grants',
  templateUrl: './grants.component.html',
  styleUrl: './grants.component.css',
  standalone: true,
  imports: [
    DatePipe,
    NgForOf,
    NgIf,
    ReactiveFormsModule,
    RouterLink,
    FormsModule
  ]
})
export class GrantsComponent {
  constructor(private router: Router, private grantService: GrantService, private teacherService: TeacherService) {
  };

  grantProfile: Grant[] = [];
  grants: Grant[] = [];
  searchValue: string = '';
  teacher: Teacher | null = null;
  filteredGrants: Grant[] = [];

  // PAGINATION
  cardsPerPage = 10;
  currentPage = 1;
  totalPages = 0;
  pages: number[] = [];

  async ngOnInit() {
    this.filteredGrants = await this.grantService.getGrants();
    this.grantProfile = [...this.filteredGrants];
    this.teacher = await this.teacherService.getTeacher();
    console.log(this.teacher?.role);
  }

  updatePagination() {
    this.totalPages = Math.ceil(this.grantProfile.length / this.cardsPerPage);
    this.pages = Array.from({length: this.totalPages}, (_, i) => i + 1);
  }

  get pagedCards() {
    const start = (this.currentPage - 1) * this.cardsPerPage;
    return this.filteredGrants.slice(start, start + this.cardsPerPage);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  goToPrevious() {
    if (this.currentPage > 1) this.currentPage--;
  }

  goToNext() {
    if (this.currentPage < this.totalPages) this.currentPage++;
  }

  selectChange(event: Event) {
    const numberSelected = event.target as HTMLSelectElement;
    if (numberSelected.value !== "All") {
      this.cardsPerPage = Number(numberSelected.value);
    } else {
      this.cardsPerPage = this.grantProfile.length;
    }
    this.updatePagination();
  }

  sorts: { field: string; dir: 'asc' | 'desc' }[] = [];

  sort(field: string) {
    const current = this.sorts[0];

    if (current?.field === field) {
      current.dir = current.dir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sorts = [{field, dir: 'asc'}];
    }
    this.applySorting();
  }

  isEmpty(val: any): boolean {
    return val === null ||
      val === undefined ||
      val === '' ||
      !String(val).trim();
  }

  selectedStatusSort: string | null = null;
  grantStatusOrder = ['cyclical', 'ongoing', 'clearing house'];

  onStatusSortChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.selectedStatusSort = select.value || null;

    this.sorts = [{field: 'status', dir: 'asc'}];
    this.applySorting();
  }

  applySorting() {
    this.filteredGrants.sort((a, b) => {
      for (const s of this.sorts) {
        let aVal: any;
        let bVal: any;
        let result = 0;

        switch (s.field) {
          case 'name':
            aVal = a.name?.trim().toLowerCase();
            bVal = b.name?.trim().toLowerCase();
            break;

          case 'link':
            aVal = a.webLink?.trim().toLowerCase();
            bVal = b.webLink?.trim().toLowerCase();
            break;

          case 'open':
            aVal = new Date(a.applicationOpenDate ?? 0).getTime();
            bVal = new Date(b.applicationOpenDate ?? 0).getTime();
            break;

          case 'close':
            aVal = new Date(a.applicationCloseDate ?? 0).getTime();
            bVal = new Date(b.applicationCloseDate ?? 0).getTime();
            break;

          case 'status':
            const selected = this.selectedStatusSort;

            aVal = a.status;
            bVal = b.status;

            if (selected) {
              if (aVal === selected && bVal !== selected) return -1;
              if (aVal !== selected && bVal === selected) return 1;
            }
            result =
              this.rank(aVal, this.grantStatusOrder) -
              this.rank(bVal, this.grantStatusOrder);
            break;
        }

        const aEmpty = this.isEmpty(aVal);
        const bEmpty = this.isEmpty(bVal);

        if (aEmpty && !bEmpty) return 1;
        if (!aEmpty && bEmpty) return -1;
        if (aEmpty && bEmpty) continue;

        if (typeof aVal === 'string') {
          result = aVal.localeCompare(bVal);
        } else {
          result = aVal - bVal;
        }

        if (result !== 0) {
          return s.dir === 'asc' ? result : -result;
        }
      }
      return 0;
    });
    this.currentPage = 1;

    this.updatePagination();
  }

  rank(value: string, order: string[]): number {
    const i = order.indexOf(value);
    return i === -1 ? order.length : i;
  }

  getSortDir(field: string): 'asc' | 'desc' | null {
    const s = this.sorts.find(sort => sort.field === field);
    return s ? s.dir : null;
  }

  // FILTERED SEARCH
  filter(search: string) {
    const query = search.toLowerCase().trim();

    if (!query) {
      this.filteredGrants = [...this.grantProfile];
    } else {
      this.filteredGrants = this.grantProfile.filter(grant =>
        (grant.status?.toLowerCase() ?? '').includes(query) ||
        (grant.name?.toLowerCase() ?? '').includes(query) ||
        (grant.webLink?.toLowerCase() ?? '').includes(query) ||
        grant.applicationOpenDate?.toString().includes(query) ||
        grant.applicationCloseDate?.toString().includes(query)
      );
    }
    this.currentPage = 1;
    this.updatePagination();
  }

  onGrantClicked(grant: Grant) {
    this.router.navigate([`/grants/${grant.id}`]);
  }

  showGrants(event: Event) {
    const selection = event.target as HTMLSelectElement;
    const value = selection.value;

    if (value === 'All Grants') {
      this.filteredGrants = [...this.grantProfile];
    } else if (value === 'Undeleted') {
      this.filteredGrants = this.grantProfile.filter(g => !g.isDeleted);
    } else if (value === 'Deleted') {
      this.filteredGrants = this.grantProfile.filter(g => g.isDeleted);
    }

    this.currentPage = 1;
    this.updatePagination();
  }
}
